import type { RefObject } from 'react';

import type { BREADCRUMBS_MODES } from '@confluence/breadcrumbs/entry-points/BreadcrumbsWithSpaceNameDropdown';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';

import { useBreadcrumbsDisplayMode } from './useBreadcrumbsDisplayMode';
import { useIsEditorToolbarDockedAtTop } from './useIsEditorToolbarDockedAtTop';
import { useIsTitleIntersectingWithHeader } from './useIsTitleIntersectingWithHeader';

type UseProgressiveHeaderType = {
	contentType: string;
	contentMode: string;
	isSpaceOverviewPage: boolean;
	isVersionHistoryPage: boolean;
	isAttachmentsPage: boolean;
	isArchived: boolean;
	isObjectSidebarShown: boolean;
	contentTypesHeaderDataLoading: boolean;
	objectHeaderRef: RefObject<HTMLDivElement | null>;
	contentId: string;
};

export const useProgressiveHeader = ({
	contentType,
	contentMode,
	isSpaceOverviewPage,
	isVersionHistoryPage,
	isAttachmentsPage,
	isArchived,
	isObjectSidebarShown,
	contentTypesHeaderDataLoading,
	objectHeaderRef,
	contentId,
}: UseProgressiveHeaderType): {
	breadcrumbsDisplayMode: BREADCRUMBS_MODES;
	isBorderlessHeader: boolean;
	isEditorToolbarShown: boolean;
} => {
	const [{ isViewMode: isLiveViewMode, isEditMode: isLiveEditMode }] = useLivePageMode();
	const isEditorToolbarDockedAtTop = useIsEditorToolbarDockedAtTop({
		defaultDockedAtTop: !isLiveEditMode,
	});

	const isPageOrBlog = contentType === 'page' || contentType === 'blogpost';
	const isViewOrViewLiveMode = contentMode === 'view' || isLiveViewMode;

	const isInEditor =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		process.env.REACT_SSR
			? contentMode === 'draft' || contentMode === 'edit' || contentMode === 'live'
			: contentMode === 'draft' || contentMode === 'edit' || isLiveEditMode;
	const isEditorToolbarShown = isInEditor && isEditorToolbarDockedAtTop;

	const shouldUseProgressiveHeader =
		(isPageOrBlog && isViewOrViewLiveMode) ||
		(isInEditor && !isEditorToolbarShown) ||
		isVersionHistoryPage ||
		isAttachmentsPage;

	const isTitleIntersectingWithHeader = useIsTitleIntersectingWithHeader({
		contentId,
		objectHeaderRef,
		shouldUseProgressiveHeader,
	});

	const breadcrumbsDisplayMode = useBreadcrumbsDisplayMode({
		isVersionHistoryPage,
		isAttachmentsPage,
		contentType,
		isSpaceOverviewPage,
		isArchived,
		isEditorToolbarShown,
		isTitleFullyVisible: !isTitleIntersectingWithHeader,
		isObjectSidebarShown,
		contentId,
		contentTypesHeaderDataLoading,
	});

	let isBorderlessHeader =
		(!isSpaceOverviewPage &&
			!isTitleIntersectingWithHeader &&
			!isObjectSidebarShown &&
			!isEditorToolbarShown) ||
		(contentTypesHeaderDataLoading && isTitleIntersectingWithHeader); // if user is scrolled down on the page such that the header is showing and transitions to another page, the header shows for a second on the new page before hiding. This prevents that.

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		// in SSR, header border should never show UNLESS you are on a editor page with toolbar docked at the top OR you are on space overview which always shows the header
		if (isEditorToolbarShown || isSpaceOverviewPage) {
			isBorderlessHeader = false;
		}
	}
	return {
		breadcrumbsDisplayMode,
		isBorderlessHeader,
		isEditorToolbarShown,
	};
};
